import React from 'react';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import { COLORS } from '../../../plTheme';
import { _t_ } from '../../../utils/translation/translation';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const driverAvg : any = {
  Intro: 2.11,
  Agenda: 1.6,
  'Level-Setting': 2.5,
  'Purchase Journey': 2.11,
  'Recommended Evaluation/Service Process': 2.61,
  'Discovery': 2.0,
  'Mutual Action Planning': 2.29,
  'Next Steps': 3.0
}

interface DiscoverySummaryProps {
  recommendationList: any;
  timePeriod: string;
}
const useStyles = makeStyles({
  root: {
    padding: '8px',
    display: 'flex',
    gap: 10,
  },
  outcomesHead: {
    color: COLORS.TEXT_HIGH_EMPHASIS,
    paddingTop: 30,
    paddingBottom: 20,
  },
  outcomeDiv: {
    paddingLeft: '5px ',
  },
  title: {
    height: '32px',
    // width: '159px',
    fontWeight: 500,
    fontSize: 22,
    // color: '#7E858E',
  },
  item: {
    // height: 480,
  },
  buttonFont: {
    fontFamily: 'Rubik',
    fontWeight: 500,
    fontSize: 14,
    textTransform: 'none',
  },
  GraphPadding: {
    paddingRight: '50px',
  },
  GraphPadding1: {
    paddingRight: '0px',
  },
  tableBody: {
    borderRadius: ' 0px 0px 6px 6px',
    border: '1.4px solid #E0E0E0',
    '@media print': {
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    },
  },
  wrapper: {
    display: 'grid',
    gridTemplateRows: '4.5fr 1fr',
    width: '475px',
  },
  wrapper1: {
    display: 'grid',
    gridTemplateRows: '4fr 1fr',
    width: '475px',
  },
  nudgeMessage: {
    lineHeight: '24px',
    fontSize: '18px',
    fontWeight: 300,
  },
  tableMessage: {
    lineHeight: '22px',
    fontSize: '16px',
    fontWeight: 300,
    fontFamily: 'Rubik',
  },
  nudgeHeader: {
    minHeight: '40px',
    width: '630px',
    overflow: 'auto',
    marginBottom: 8,
  },
  avatar: {
    width: 80,
    height: 80,
    fontSize: '30px',
  },
  wrapperDiv: {
    fontSize: 16,
    color: 'white',
    fontWeight: 400,
    padding: 4,
    width: 70,
    borderRadius: 4,
  },
  scoreBarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
  },
  scoreText: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Rubik',
  },
  scoreBar: {
    position: 'relative',
    width: '76px',
    height: '8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#0B69FF',
    color: theme.palette.common.white,
    fontSize: 18,
    fontWeight: 500,
    fontFamily: 'Rubik',
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
    '@media print': {
      backgroundColor: '#0B69FF !important',
      color: `${theme.palette.common.white} !important`,
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 15,
    fontFamily: 'Rubik',
    border: 0,
    '@media print': {
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const DiscoverySummary: React.FC<DiscoverySummaryProps> = ({
  recommendationList,
  timePeriod,
}: any) => {
  const classes = useStyles();

  // Fixed width calculations based on isReport
  const tableWidth = 720;
  const containerWidth = 720;

  const drivers = [
    'Intro',
    'Purchase Journey',
    'Agenda',
    'Level-Setting',
    'Recommended Evaluation/Service Process',
    'Discovery',
    'Mutual Action Planning',
    'Next Steps',
  ];

  function getAverage(data: any[], keyName: string, score: any): string {
    const values = data
      ?.map((item) => item[keyName])
      ?.filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== 'N/A',
      )
      ?.map((value) => parseFloat(value))
      ?.filter((value) => !isNaN(value));

    if (values.length === 0 || isNaN(score)) {
      return '#bfc0c3'; // No valid values found
    }

    const sum = values.reduce((acc, val) => acc + val, 0);
    const average = sum / data.length; // Divide by total number of records

    let avg = parseFloat(average.toFixed(2));
    const deviation = avg * 0.1;
    let redColorVal = avg - deviation,
      orangeColorVal = avg + deviation;
    let bgColor = '';
    if (score < redColorVal) {
      bgColor = COLORS.AVATARS_RED_2;
    } else if (score >= redColorVal && score < orangeColorVal) {
      bgColor = COLORS.TERTIARY_COLOR_ORANGE;
    } else bgColor = COLORS.SUCCESS_PRESSED;

    return bgColor;
  }

  function renderScoreBar(score: any, driver: string, finatData: any[]) {
    const numericScore = parseFloat(score);
    const isValidScore =
      !isNaN(numericScore) &&
      score !== null &&
      score !== undefined &&
      score !== '';

    if (!isValidScore) {
      return (
        <div className={classes.scoreBarContainer}>
          <span className={classes.scoreText} style={{ color: '#9ca3af' }}>
            N/A
          </span>
          <div className={classes.scoreBar}>
            <div
              className={classes.scoreBarFill}
              style={{
                width: '0%',
                backgroundColor: '#bfc0c3',
              }}
            />
          </div>
        </div>
      );
    }

    const percentage = Math.min(Math.max((numericScore / 10) * 100, 0), 100);
    const backgroundColor = getAverage(finatData, driver, numericScore);

    return (
      <div className={classes.scoreBarContainer}>
        <span className={classes.scoreText} style={{ color: backgroundColor }}>
          {numericScore % 1 === 0 ? numericScore : numericScore.toFixed(1)}
        </span>
        <div className={classes.scoreBar}>
          <div
            className={classes.scoreBarFill}
            style={{
              width: `${percentage}%`,
              backgroundColor: backgroundColor,
            }}
          />
        </div>
      </div>
    );
  }

  function extractScores(data: any) {
    let repScoreList: any = [];
    data?.forEach((item: any) => {
      const result: any = {};
      const coachingPlanList =
        item?.['Coaching Plan']?.Opportunities[0]?.Recommendations;
      result['Rep Name'] = item?.['Rep Name'];

      for (const key in coachingPlanList) {
        let score =
          coachingPlanList?.[key]?.['Score'] ||
          coachingPlanList?.[key]?.['Score (1-10)'];
        if (coachingPlanList[key] && score) {
          result[key] = score;
        }
      }
      repScoreList.push(result);
    });
    return repScoreList;
  }
  const finatData = extractScores(recommendationList);
  return (
    <>
      <div className={classes.item}>
        <Grid container direction="column" className={classes.root}>
          <Grid item style={{ flex: 1 }}>
            <Grid
              container
              direction="row"
              justify="center"
              style={{ alignItems: 'center', margin: '10px  4px 10px 0 ' }}
            >
              <Typography variant="h3" className={classes.title}>
                {`Summary : Driver (${timePeriod})`}
              </Typography>
            </Grid>
          </Grid>
          <Grid item style={{ flex: 1.5 }}>
            <Grid
              container
              direction="row"
              style={{
                width: containerWidth,
                overflow: 'auto',
                top: '429px',
                left: '410px',
              }}
            >
              <TableContainer
                component={Paper}
                style={{
                  width: '100%',
                  maxWidth: containerWidth,
                  overflow: 'auto',
                }}
              >
                <Table
                  sx={{
                    minWidth: tableWidth,
                    width: '100%',
                    tableLayout: 'fixed', // Fixed layout for consistent column widths
                  }}
                  aria-label="customized table"
                >
                  <TableHead>
                    <TableRow>
                      <StyledTableCell
                        align="left"
                        sx={{
                          width: '31%',
                          minWidth: '120px',
                        }}
                      >
                        <Typography
                          className={classes.tableMessage}
                          style={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: 16,
                          }}
                        >
                          Sales Methadology
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{
                          width: '23%',
                          minWidth: '120px',
                        }}
                      >
                        <Typography
                          className={classes.tableMessage}
                          style={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: 16,
                          }}
                        >
                          Ryan
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{
                          width: '23%',
                          minWidth: '120px',
                        }}
                      >
                        <Typography
                          className={classes.tableMessage}
                          style={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: 16,
                          }}
                        >
                          Charles
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{
                          width: '23%',
                          minWidth: '120px',
                        }}
                      >
                        <Typography
                          className={classes.tableMessage}
                          style={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: 16,
                          }}
                        >
                          Juan
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {Array.from({ length: 8 }, (_, index) => {
                      const driver = drivers[index];
                      const ryanScore = finatData?.find(
                        (r: any) => r?.['Rep Name'] == 'Ryan Morgan',
                      )?.[driver];
                      const charlesScore = finatData?.find(
                        (r: any) => r?.['Rep Name'] == 'Charles Adams',
                      )?.[driver];
                      const juanScore = finatData?.find(
                        (r: any) => r?.['Rep Name'] == 'Juan Forero',
                      )?.[driver];
                      return (
                        <StyledTableRow key={`imp_opp_${index}`}>
                          <StyledTableCell align="left" scope="row">
                            <div>
                              <Typography
                                className={classes.tableMessage}
                                style={{ fontSize: 14, fontWeight: 500 }}
                              >
                                {drivers[index]}
                              </Typography>
                              <p
                                style={{
                                  fontSize: '12px',
                                  color: 'rgb(107, 114, 128)',
                                  margin: 0,
                                  fontFamily: 'Rubik',
                                  fontWeight: 500,
                                }}
                              >
                                Avg: {driverAvg[drivers[index]]}
                              </p>
                            </div>
                          </StyledTableCell>
                          <StyledTableCell align="center" scope="row">
                            <Grid
                              style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '8px',
                              }}
                            >
                              {renderScoreBar(ryanScore, driver, finatData)}
                            </Grid>
                          </StyledTableCell>
                          <StyledTableCell align="center" scope="row">
                            <Grid
                              style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '8px',
                              }}
                            >
                              {renderScoreBar(charlesScore, driver, finatData)}
                            </Grid>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Grid
                              style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '8px',
                              }}
                            >
                              {renderScoreBar(juanScore, driver, finatData)}
                            </Grid>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default DiscoverySummary;
